import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { COLORS } from '../utils/constants';
import { tradingAPI } from '../services/api';
import {
  ArrowLeft, TrendingUp, TrendingDown, Globe, Twitter, Send,
  DollarSign, BarChart3, Activity, Target, Award, Clock,
  ChevronUp, ChevronDown, ExternalLink, Star, Share2, Zap
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import MobileCoinDetailPage from '../components/mobile/MobileCoinDetailPage';

const CoinDetailPage = () => {
  const { coinId } = useParams();
  const navigate = useNavigate();
  
  const [coin, setCoin] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(7);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Use mobile component on mobile devices
  if (isMobile) {
    return <MobileCoinDetailPage />;
  }

  // Desktop version continues below...
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    loadCoinData();
  }, [coinId, selectedPeriod]);

  const loadCoinData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [detailsRes, historyRes] = await Promise.all([
        tradingAPI.getCoinDetails(coinId),
        tradingAPI.getCoinHistory(coinId, selectedPeriod),
      ]);
      
      setCoin(detailsRes.data);
      setHistory(historyRes.data?.history || []);
    } catch (err) {
      console.error('Failed to load coin data:', err);
      setError('Не удалось загрузить данные о монете');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value, decimals = 2) => {
    if (!value) return '$0.00';
    if (value >= 1) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(value);
    }
    return `$${value.toPrecision(4)}`;
  };

  const formatCompact = (value) => {
    if (!value) return '$0';
    if (value >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
    if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
    if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
    if (value >= 1e3) return `$${(value / 1e3).toFixed(1)}K`;
    return `$${value.toFixed(2)}`;
  };

  const formatNumber = (value) => {
    if (!value) return '0';
    return new Intl.NumberFormat('en-US').format(value);
  };

  const formatPercent = (value) => {
    if (!value) return '0.00%';
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('ru-RU', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh', flexDirection: 'column', gap: '16px' }}>
        <div style={{
          width: '48px', height: '48px',
          border: `3px solid ${COLORS.border}`,
          borderTop: `3px solid ${COLORS.primary}`,
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
        }} />
        <span style={{ color: COLORS.textSecondary }}>Загрузка данных о {coinId?.toUpperCase()}...</span>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error || !coin) {
    return (
      <div style={{ maxWidth: '600px', margin: '100px auto', textAlign: 'center', padding: '40px' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>😕</div>
        <h2 style={{ color: '#eaecef', marginBottom: '12px' }}>{error || 'Монета не найдена'}</h2>
        <p style={{ color: '#848e9c', marginBottom: '24px' }}>Попробуйте другую монету или вернитесь назад</p>
        <button
          onClick={() => navigate(-1)}
          style={{
            padding: '12px 24px', backgroundColor: '#f0b90b', color: '#0b0e11',
            border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer',
          }}
        >
          ← Назад
        </button>
      </div>
    );
  }

  const priceChange24h = coin.price_change_percentage_24h || 0;
  const isPositive = priceChange24h >= 0;

  // Chart data
  const chartData = history.map(item => ({
    date: new Date(item.timestamp).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' }),
    price: item.price,
    volume: item.volume,
  }));

  const minPrice = Math.min(...chartData.map(d => d.price));
  const maxPrice = Math.max(...chartData.map(d => d.price));

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px 40px' }}>
      {/* Header with Back Button */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '10px 16px', backgroundColor: '#1e2329',
            border: '1px solid #2b3139', borderRadius: '8px',
            color: '#eaecef', cursor: 'pointer', fontSize: '14px',
          }}
        >
          <ArrowLeft size={18} /> Назад
        </button>
        
        <div style={{ flex: 1 }} />
        
        <button
          onClick={() => setIsFavorite(!isFavorite)}
          style={{
            padding: '10px', backgroundColor: isFavorite ? '#f0b90b20' : '#1e2329',
            border: `1px solid ${isFavorite ? '#f0b90b' : '#2b3139'}`,
            borderRadius: '8px', cursor: 'pointer',
          }}
        >
          <Star size={18} color={isFavorite ? '#f0b90b' : '#848e9c'} fill={isFavorite ? '#f0b90b' : 'none'} />
        </button>
        
        <Link to={`/trading?symbol=${coin.symbol}USDT`}>
          <button style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '10px 20px', background: 'linear-gradient(135deg, #f0b90b 0%, #d4a300 100%)',
            border: 'none', borderRadius: '8px',
            color: '#0b0e11', fontWeight: '700', cursor: 'pointer', fontSize: '14px',
          }}>
            <TrendingUp size={18} /> Торговать {coin.symbol}
          </button>
        </Link>
      </div>

      {/* Main Info Card */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 400px',
        gap: '24px',
        marginBottom: '24px',
      }}>
        {/* Left: Coin Info */}
        <div style={{
          backgroundColor: '#1e2329',
          border: '1px solid #2b3139',
          borderRadius: '16px',
          padding: '24px',
        }}>
          {/* Coin Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
            {coin.image && (
              <img src={coin.image} alt={coin.name} style={{ width: '64px', height: '64px', borderRadius: '50%' }} />
            )}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
                <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#eaecef', margin: 0 }}>{coin.name}</h1>
                <span style={{
                  padding: '4px 10px', backgroundColor: '#2b3139', borderRadius: '6px',
                  color: '#848e9c', fontSize: '14px', fontWeight: '600',
                }}>
                  {coin.symbol}
                </span>
                {coin.market_cap_rank && (
                  <span style={{
                    padding: '4px 10px', backgroundColor: '#f0b90b20', borderRadius: '6px',
                    color: '#f0b90b', fontSize: '12px', fontWeight: '600',
                  }}>
                    Ранг #{coin.market_cap_rank}
                  </span>
                )}
              </div>
              <div style={{ color: '#848e9c', fontSize: '14px' }}>
                {coin.categories?.slice(0, 3).join(' • ')}
              </div>
            </div>
          </div>

          {/* Current Price */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ fontSize: '42px', fontWeight: '700', color: '#eaecef', marginBottom: '8px' }}>
              {formatCurrency(coin.current_price)}
            </div>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              padding: '8px 16px', borderRadius: '24px',
              backgroundColor: isPositive ? 'rgba(14, 203, 129, 0.15)' : 'rgba(246, 70, 93, 0.15)',
            }}>
              {isPositive ? <TrendingUp size={18} color="#0ecb81" /> : <TrendingDown size={18} color="#f6465d" />}
              <span style={{ color: isPositive ? '#0ecb81' : '#f6465d', fontWeight: '600', fontSize: '16px' }}>
                {formatPercent(priceChange24h)} (24ч)
              </span>
            </div>
          </div>

          {/* Price Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
            <div style={{ padding: '16px', backgroundColor: '#0b0e11', borderRadius: '12px' }}>
              <div style={{ color: '#848e9c', fontSize: '12px', marginBottom: '6px' }}>24ч Максимум</div>
              <div style={{ color: '#0ecb81', fontSize: '16px', fontWeight: '600' }}>{formatCurrency(coin.high_24h)}</div>
            </div>
            <div style={{ padding: '16px', backgroundColor: '#0b0e11', borderRadius: '12px' }}>
              <div style={{ color: '#848e9c', fontSize: '12px', marginBottom: '6px' }}>24ч Минимум</div>
              <div style={{ color: '#f6465d', fontSize: '16px', fontWeight: '600' }}>{formatCurrency(coin.low_24h)}</div>
            </div>
            <div style={{ padding: '16px', backgroundColor: '#0b0e11', borderRadius: '12px' }}>
              <div style={{ color: '#848e9c', fontSize: '12px', marginBottom: '6px' }}>7д Изменение</div>
              <div style={{ color: (coin.price_change_percentage_7d || 0) >= 0 ? '#0ecb81' : '#f6465d', fontSize: '16px', fontWeight: '600' }}>
                {formatPercent(coin.price_change_percentage_7d)}
              </div>
            </div>
            <div style={{ padding: '16px', backgroundColor: '#0b0e11', borderRadius: '12px' }}>
              <div style={{ color: '#848e9c', fontSize: '12px', marginBottom: '6px' }}>30д Изменение</div>
              <div style={{ color: (coin.price_change_percentage_30d || 0) >= 0 ? '#0ecb81' : '#f6465d', fontSize: '16px', fontWeight: '600' }}>
                {formatPercent(coin.price_change_percentage_30d)}
              </div>
            </div>
          </div>

          {/* ATH / ATL */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
            <div style={{
              padding: '16px', backgroundColor: '#0b0e11', borderRadius: '12px',
              border: '1px solid #0ecb8130',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <Award size={16} color="#0ecb81" />
                <span style={{ color: '#848e9c', fontSize: '12px' }}>Исторический максимум (ATH)</span>
              </div>
              <div style={{ color: '#0ecb81', fontSize: '20px', fontWeight: '700', marginBottom: '4px' }}>
                {formatCurrency(coin.ath)}
              </div>
              <div style={{ color: '#848e9c', fontSize: '11px' }}>
                {formatDate(coin.ath_date)} • <span style={{ color: '#f6465d' }}>{formatPercent(coin.ath_change_percentage)}</span>
              </div>
            </div>
            <div style={{
              padding: '16px', backgroundColor: '#0b0e11', borderRadius: '12px',
              border: '1px solid #f6465d30',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <Target size={16} color="#f6465d" />
                <span style={{ color: '#848e9c', fontSize: '12px' }}>Исторический минимум (ATL)</span>
              </div>
              <div style={{ color: '#f6465d', fontSize: '20px', fontWeight: '700', marginBottom: '4px' }}>
                {formatCurrency(coin.atl)}
              </div>
              <div style={{ color: '#848e9c', fontSize: '11px' }}>
                {formatDate(coin.atl_date)} • <span style={{ color: '#0ecb81' }}>{formatPercent(coin.atl_change_percentage)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Market Stats */}
        <div style={{
          backgroundColor: '#1e2329',
          border: '1px solid #2b3139',
          borderRadius: '16px',
          padding: '24px',
        }}>
          <h3 style={{ color: '#eaecef', fontSize: '16px', fontWeight: '700', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <BarChart3 size={18} color="#f0b90b" /> Рыночная статистика
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #2b3139' }}>
              <span style={{ color: '#848e9c', fontSize: '14px' }}>Рыночная капитализация</span>
              <span style={{ color: '#eaecef', fontSize: '14px', fontWeight: '600' }}>{formatCompact(coin.market_cap)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #2b3139' }}>
              <span style={{ color: '#848e9c', fontSize: '14px' }}>FDV (Полностью разводнённая)</span>
              <span style={{ color: '#eaecef', fontSize: '14px', fontWeight: '600' }}>{formatCompact(coin.fully_diluted_valuation)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #2b3139' }}>
              <span style={{ color: '#848e9c', fontSize: '14px' }}>Объём торгов (24ч)</span>
              <span style={{ color: '#eaecef', fontSize: '14px', fontWeight: '600' }}>{formatCompact(coin.total_volume)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #2b3139' }}>
              <span style={{ color: '#848e9c', fontSize: '14px' }}>В обращении</span>
              <span style={{ color: '#eaecef', fontSize: '14px', fontWeight: '600' }}>{formatNumber(coin.circulating_supply)} {coin.symbol}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #2b3139' }}>
              <span style={{ color: '#848e9c', fontSize: '14px' }}>Общее предложение</span>
              <span style={{ color: '#eaecef', fontSize: '14px', fontWeight: '600' }}>
                {coin.total_supply ? `${formatNumber(coin.total_supply)} ${coin.symbol}` : '∞'}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0' }}>
              <span style={{ color: '#848e9c', fontSize: '14px' }}>Макс. предложение</span>
              <span style={{ color: '#eaecef', fontSize: '14px', fontWeight: '600' }}>
                {coin.max_supply ? `${formatNumber(coin.max_supply)} ${coin.symbol}` : '∞'}
              </span>
            </div>
          </div>

          {/* Links */}
          {coin.links && (
            <div style={{ marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #2b3139' }}>
              <h4 style={{ color: '#848e9c', fontSize: '12px', marginBottom: '12px', fontWeight: '600' }}>ССЫЛКИ</h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {coin.links.homepage && (
                  <a href={coin.links.homepage} target="_blank" rel="noopener noreferrer" style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    padding: '8px 12px', backgroundColor: '#0b0e11', borderRadius: '8px',
                    color: '#eaecef', textDecoration: 'none', fontSize: '12px',
                  }}>
                    <Globe size={14} /> Сайт <ExternalLink size={12} color="#848e9c" />
                  </a>
                )}
                {coin.links.twitter && (
                  <a href={`https://twitter.com/${coin.links.twitter}`} target="_blank" rel="noopener noreferrer" style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    padding: '8px 12px', backgroundColor: '#0b0e11', borderRadius: '8px',
                    color: '#1DA1F2', textDecoration: 'none', fontSize: '12px',
                  }}>
                    <Twitter size={14} /> Twitter <ExternalLink size={12} color="#848e9c" />
                  </a>
                )}
                {coin.links.telegram && (
                  <a href={`https://t.me/${coin.links.telegram}`} target="_blank" rel="noopener noreferrer" style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    padding: '8px 12px', backgroundColor: '#0b0e11', borderRadius: '8px',
                    color: '#0088cc', textDecoration: 'none', fontSize: '12px',
                  }}>
                    <Send size={14} /> Telegram <ExternalLink size={12} color="#848e9c" />
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Community Stats */}
          {coin.community_data && (coin.community_data.twitter_followers || coin.community_data.reddit_subscribers) && (
            <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #2b3139' }}>
              <h4 style={{ color: '#848e9c', fontSize: '12px', marginBottom: '12px', fontWeight: '600' }}>СООБЩЕСТВО</h4>
              <div style={{ display: 'flex', gap: '16px' }}>
                {coin.community_data.twitter_followers && (
                  <div>
                    <div style={{ color: '#1DA1F2', fontSize: '16px', fontWeight: '700' }}>
                      {formatNumber(coin.community_data.twitter_followers)}
                    </div>
                    <div style={{ color: '#848e9c', fontSize: '11px' }}>Twitter подписчиков</div>
                  </div>
                )}
                {coin.community_data.reddit_subscribers && (
                  <div>
                    <div style={{ color: '#FF4500', fontSize: '16px', fontWeight: '700' }}>
                      {formatNumber(coin.community_data.reddit_subscribers)}
                    </div>
                    <div style={{ color: '#848e9c', fontSize: '11px' }}>Reddit подписчиков</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Price Chart */}
      <div style={{
        backgroundColor: '#1e2329',
        border: '1px solid #2b3139',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ color: '#eaecef', fontSize: '16px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
            <Activity size={18} color="#f0b90b" /> График цены {coin.symbol}
          </h3>
          <div style={{ display: 'flex', gap: '8px' }}>
            {[
              { days: 1, label: '24ч' },
              { days: 7, label: '7д' },
              { days: 30, label: '30д' },
              { days: 90, label: '90д' },
              { days: 365, label: '1г' },
            ].map(period => (
              <button
                key={period.days}
                onClick={() => setSelectedPeriod(period.days)}
                style={{
                  padding: '8px 14px',
                  backgroundColor: selectedPeriod === period.days ? '#f0b90b' : '#0b0e11',
                  border: selectedPeriod === period.days ? '1px solid #f0b90b' : '1px solid #2b3139',
                  borderRadius: '6px',
                  color: selectedPeriod === period.days ? '#0b0e11' : '#848e9c',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                {period.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ height: '350px' }}>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={isPositive ? '#0ecb81' : '#f6465d'} stopOpacity={0.3}/>
                    <stop offset="95%" stopColor={isPositive ? '#0ecb81' : '#f6465d'} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <XAxis 
                  dataKey="date" 
                  stroke="#5e6673" 
                  fontSize={11} 
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis 
                  stroke="#5e6673" 
                  fontSize={11} 
                  tickLine={false}
                  axisLine={false}
                  domain={[minPrice * 0.99, maxPrice * 1.01]}
                  tickFormatter={(val) => formatCurrency(val)}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1e2329',
                    border: '1px solid #2b3139',
                    borderRadius: '8px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                  }}
                  labelStyle={{ color: '#848e9c' }}
                  formatter={(value) => [formatCurrency(value), 'Цена']}
                />
                <Area
                  type="monotone"
                  dataKey="price"
                  stroke={isPositive ? '#0ecb81' : '#f6465d'}
                  strokeWidth={2}
                  fill="url(#colorPrice)"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#848e9c' }}>
              Нет данных для графика
            </div>
          )}
        </div>
      </div>

      {/* Description */}
      {coin.description && (
        <div style={{
          backgroundColor: '#1e2329',
          border: '1px solid #2b3139',
          borderRadius: '16px',
          padding: '24px',
        }}>
          <h3 style={{ color: '#eaecef', fontSize: '16px', fontWeight: '700', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Zap size={18} color="#f0b90b" /> О {coin.name}
          </h3>
          <p style={{ color: '#b7bdc6', fontSize: '14px', lineHeight: '1.7', margin: 0 }}>
            {coin.description}
          </p>
        </div>
      )}
    </div>
  );
};

export default CoinDetailPage;
